import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import type { ClimbFilters, GroupBy } from '../../hooks/useClimbFilters';
import type { RouteSetter } from '../../types/route-setter';

interface ClimbsFilterPanelProps {
  filters: ClimbFilters;
  updateFilters: (newFilters: Partial<ClimbFilters>) => void;
  context: 'gym' | 'user-profile';
  gradeOptions: string[];
  routeSetterOptions: RouteSetter[];
  holdColorOptions: string[];
  daysAgoOptions: number[];
  userGyms: Array<{ id: string | number; name: string; slug: string }>;
  isStaffMember: boolean;
  isAuthenticated: boolean;
}

export default function ClimbsFilterPanel({
  filters,
  updateFilters,
  context,
  gradeOptions,
  routeSetterOptions,
  holdColorOptions,
  daysAgoOptions,
  userGyms,
  isStaffMember,
  isAuthenticated,
}: ClimbsFilterPanelProps) {
  const { t } = useTranslation();

  const {
    sortBy,
    groupBy,
    gradeRange,
    selectedRouteSetter,
    selectedHoldColor,
    selectedLocation,
    showUnofficial,
    showOnlyNotSent,
    olderThanDays,
  } = filters;

  return (
    <div className="border rounded-lg p-4 bg-muted/30">
      <div className={`grid grid-cols-1 ${context === 'user-profile' ? 'md:grid-cols-2' : 'md:grid-cols-3'} gap-4`}>
        {context === 'user-profile' && userGyms.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('climbs.filterByGym')}
            </label>
            <select
              value={selectedLocation || 'all'}
              onChange={(e) => {
                updateFilters({ selectedLocation: e.target.value });
              }}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="all">{t('climbs.allGyms')}</option>
              {userGyms.map(gym => (
                <option key={gym.id} value={gym.id.toString()}>{gym.name}</option>
              ))}
            </select>
          </div>
        )}

        {context === 'gym' && (
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('climbs.sortBy')}
            </label>
            <select
              value={sortBy}
              onChange={(e) => {
                updateFilters({ sortBy: e.target.value as ClimbFilters['sortBy'] });
              }}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="newest">{t('climbs.sortNewest')}</option>
              <option value="oldest">{t('climbs.sortOldest')}</option>
              <option value="easiest">{t('climbs.sortEasiest')}</option>
              <option value="hardest">{t('climbs.sortHardest')}</option>
            </select>
          </div>
        )}

        {context === 'gym' && (
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('climbs.groupBy')}
            </label>
            <select
              value={groupBy}
              onChange={(e) => {
                updateFilters({ groupBy: e.target.value as GroupBy });
              }}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="none">{t('climbs.groupByNone')}</option>
              <option value="setter">{t('climbs.groupBySetter')}</option>
              <option value="grade">{t('climbs.groupByGrade')}</option>
              <option value="zone">{t('climbs.groupByZone')}</option>
            </select>
          </div>
        )}

        {gradeOptions.length > 1 && (() => {
          const loIdx = gradeRange ? Math.max(0, gradeOptions.indexOf(gradeRange[0])) : 0;
          const hiIdx = gradeRange ? Math.max(0, gradeOptions.indexOf(gradeRange[1])) : gradeOptions.length - 1;
          const safeLoIdx = loIdx === -1 ? 0 : loIdx;
          const safeHiIdx = hiIdx === -1 ? gradeOptions.length - 1 : hiIdx;
          return (
            <div>
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm font-medium">{t('climbs.grade')}</label>
                <div className="flex items-center gap-1.5 text-sm">
                  {gradeRange ? (
                    <>
                      <span className="font-medium">{gradeOptions[safeLoIdx]}</span>
                      {safeLoIdx !== safeHiIdx && (
                        <>
                          <span className="text-muted-foreground">–</span>
                          <span className="font-medium">{gradeOptions[safeHiIdx]}</span>
                        </>
                      )}
                      <button
                        onClick={() => updateFilters({ gradeRange: null })}
                        className="ml-1 text-muted-foreground hover:text-foreground"
                        aria-label={t('climbs.allGrades')}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <span className="text-muted-foreground">{t('climbs.allGrades')}</span>
                  )}
                </div>
              </div>
              <Slider
                min={0}
                max={gradeOptions.length - 1}
                step={1}
                value={[safeLoIdx, safeHiIdx]}
                onValueChange={([lo, hi]) => {
                  const isFullRange = lo === 0 && hi === gradeOptions.length - 1;
                  updateFilters({
                    gradeRange: isFullRange ? null : [gradeOptions[lo], gradeOptions[hi]],
                  });
                }}
                className="py-1"
              />
              <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                <span>{gradeOptions[0]}</span>
                <span>{gradeOptions[gradeOptions.length - 1]}</span>
              </div>
            </div>
          );
        })()}

        {context === 'gym' && (
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('climbs.routeSetter')}
            </label>
            <select
              value={selectedRouteSetter}
              onChange={(e) => {
                updateFilters({ selectedRouteSetter: e.target.value });
              }}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="all">{t('climbs.allRouteSetters')}</option>
              {routeSetterOptions.map(setter => (
                <option key={setter.id} value={setter.id}>
                  {setter.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {holdColorOptions.length > 0 && (
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('climbs.holdColor')}
            </label>
            <select
              value={selectedHoldColor}
              onChange={(e) => {
                updateFilters({ selectedHoldColor: e.target.value });
              }}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="all">{t('climbs.allHoldColors')}</option>
              {holdColorOptions.map(color => (
                <option key={color} value={color}>{color}</option>
              ))}
            </select>
          </div>
        )}

      </div>

      {/* Staff-only: Older than X days filter */}
      {isStaffMember && context === 'gym' && daysAgoOptions.length > 0 && (
        <div>
          <label className="block text-sm font-medium mb-2">
            {t('climbs.olderThanDays')}
          </label>
          <select
            value={olderThanDays}
            onChange={(e) => {
              updateFilters({ olderThanDays: e.target.value || undefined });
            }}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">{t('climbs.allClimbs')}</option>
            {daysAgoOptions.map(days => (
              <option key={days} value={days.toString()}>
                {t('climbs.olderThanDaysOption', { count: days })}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Official Status Filter */}
      <div className="mt-4 space-y-3">
        {context === 'gym' && (
          <div className="flex items-center space-x-2">
            <Switch
              id="show-unofficial"
              checked={showUnofficial}
              onCheckedChange={(checked) => {
                updateFilters({ showUnofficial: checked });
              }}
            />
            <Label htmlFor="show-unofficial" className="text-sm font-medium">
              {t('climbs.showUnofficial')}
            </Label>
          </div>
        )}

        {isAuthenticated && (
          <div className="flex items-center space-x-2">
            <Switch
              id="show-only-not-sent"
              checked={showOnlyNotSent}
              onCheckedChange={(checked) => {
                updateFilters({ showOnlyNotSent: checked });
              }}
            />
            <Label htmlFor="show-only-not-sent" className="text-sm font-medium">
              {t('climbs.showOnlyNotSent')}
            </Label>
          </div>
        )}
      </div>
    </div>
  );
}
